/**
 * Textos POR DEFECTO de las secciones del builder, en portugués — SOLO Brasil.
 *
 * El builder del admin guarda en la base, al soltar una sección, sus textos de
 * ejemplo en español ("Encuentra tu próximo auto", las preguntas del FAQ, los
 * links del navbar…). Además, Craft completa con `craft.props` de cada
 * componente las props que un árbol viejo no trae. Para una automotora de
 * Brasil que no editó esos textos, el sitio salía en español.
 *
 * Acá se traduce, al renderizar y SOLO para tenants de Brasil, todo texto que
 * sea EXACTAMENTE uno de esos textos de fábrica, o que calce con uno de los
 * PATRONES de abajo (textos de fábrica con una parte variable, como el
 * copyright con el nombre de la automotora). Lo que la automotora escribió no
 * coincide con un texto de fábrica y queda como está. Nada se guarda: la base
 * no cambia.
 *
 * La lista sale de:
 * - builder2/i18n/defaultsPt.{a,b}.ts — copia de los mapas del admin (el admin
 *   los usa para guardar en pt las secciones nuevas de un tenant BR; acá se
 *   usan para los árboles que ya se guardaron en español). Mandan éstos.
 * - `site.builderDefaults` de es/pt (mismas claves): los pocos textos de
 *   fábrica que esos mapas no traen.
 *
 * Para el resto de los tenants no se llama nunca (el que llama gatea por país),
 * y aunque se llamara, no toca nada que no sea un texto de fábrica idéntico.
 */
import { siteEs } from '@/i18n/locales/es/site';
import { sitePt } from '@/i18n/locales/pt/site';
import { DEFAULTS_PT_A } from '../i18n/defaultsPt.a';
import { DEFAULTS_PT_B } from '../i18n/defaultsPt.b';

/**
 * Valores que el código del sitio COMPARA (no son solo texto a mostrar): nunca
 * se traducen aunque algún mapa los traiga. 'Nuevo' es el centinela de
 * `newBadgeText` (VehicleVerticalCard: `=== 'Nuevo'` rota las etiquetas
 * automáticas, que ya salen en pt por t()); traducirlo dejaba fija "Novo".
 */
const NO_TRADUCIR = new Set(['Nuevo', 'Todos', 'Publicado', 'Reservado', 'Vendido']);

/**
 * Textos de fábrica con partes variables (nombre de la automotora, año): el
 * admin los arma con el nombre del tenant (builderTemplates.ts, Footer.tsx,
 * WhyChooseUs.tsx), así que nunca coinciden exacto. Se mira primero el mapa
 * (p. ej. "…confiaron en nosotros" tiene su entrada) y después esto.
 *
 * La parte variable tiene que ser un NOMBRE: si trae palabras de frase ("para",
 * "tu", "y", "nuestro"…) es un texto que la automotora reescribió, y traducir
 * solo el comienzo lo dejaba mitad en pt y mitad en es. Ese queda como está.
 */
const PALABRAS_DE_FRASE =
  /(^|\s)(tu|tus|su|sus|mi|mis|nuestro|nuestra|nuestros|nuestras|y|en|con|los|las|del|al|un|una)(\s|$)/i;
const esNombre = (s: string): boolean => mapaEsPt().has(s) || !PALABRAS_DE_FRASE.test(s);

// El nombre de respaldo ('Nuestra Automotora', sin nombre cargado) también sale en pt.
const nombre = (s: string): string => mapaEsPt().get(s) ?? s;

const PATRONES: Array<[RegExp, (m: RegExpMatchArray) => string | null]> = [
  // Footer: "© 2026 <automotora>. Todos los derechos reservados."
  [/^© (\d{4}) (.+)\. Todos los derechos reservados\.$/, (m) => `© ${m[1]} ${m[2]}. Todos os direitos reservados.`],
  // Por qué elegirnos / plantillas: "¿Por qué elegir <automotora>?"
  [/^¿Por qué elegir (.+)\?$/, (m) => (esNombre(m[1]) ? `Por que escolher ${nombre(m[1])}?` : null)],
  // Testimonios de las plantillas: "Experiencias reales de quienes confiaron en <automotora>"
  [/^Experiencias reales de quienes confiaron en (.+)$/, (m) =>
    esNombre(m[1]) ? `Experiências reais de quem confiou em ${nombre(m[1])}` : null],
  // Nosotros (equipo): "Conoce a las personas detrás de <automotora>"
  [/^Conoce a las personas detrás de (.+)$/, (m) =>
    esNombre(m[1]) ? `Conheça as pessoas por trás de ${nombre(m[1])}` : null],
  // Héroe de utils/clientDefaults del admin: "Bienvenido a <automotora>"
  [/^Bienvenido a (.+)$/, (m) => (esNombre(m[1]) ? `Bem-vindo a ${nombre(m[1])}` : null)],
];

let mapa: Map<string, string> | null = null;

/** es → pt, armado una sola vez. */
const mapaEsPt = (): Map<string, string> => {
  if (mapa) return mapa;
  const m = new Map<string, string>();
  const es = siteEs.builderDefaults as Record<string, string>;
  const pt = sitePt.builderDefaults as Record<string, string>;
  for (const clave of Object.keys(es)) {
    const destino = pt[clave];
    if (typeof destino === 'string' && destino && destino !== es[clave]) m.set(es[clave], destino);
  }
  for (const [origen, destino] of Object.entries({ ...DEFAULTS_PT_A, ...DEFAULTS_PT_B })) {
    if (destino && destino !== origen) m.set(origen, destino);
  }
  for (const s of NO_TRADUCIR) m.delete(s);
  mapa = m;
  return m;
};

/** Un string: por coincidencia exacta y, si no, por patrón. */
const traducirTexto = (s: string): string => {
  const directo = mapaEsPt().get(s);
  if (directo !== undefined) return directo;
  for (const [re, fn] of PATRONES) {
    const m = s.match(re);
    if (m) return fn(m) ?? s;
  }
  return s;
};

/** El texto de fábrica en portugués, o el mismo texto si no es uno de fábrica. */
export const textoDefaultPt = (texto: string): string => traducirTexto(texto);

const esObjetoPlano = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && Object.getPrototypeOf(v) === Object.prototype;

/**
 * Traduce en profundidad (strings sueltos, arrays y objetos planos). Devuelve
 * la MISMA referencia si no cambió nada, para no recrear lo que no hace falta.
 */
const traducir = (v: unknown): unknown => {
  if (typeof v === 'string') return traducirTexto(v);
  if (Array.isArray(v)) {
    let cambio = false;
    const out = v.map((x) => {
      const y = traducir(x);
      if (y !== x) cambio = true;
      return y;
    });
    return cambio ? out : v;
  }
  if (esObjetoPlano(v)) {
    let cambio = false;
    const out: Record<string, unknown> = {};
    for (const [k, x] of Object.entries(v)) {
      const y = traducir(x);
      if (y !== x) cambio = true;
      out[k] = y;
    }
    return cambio ? out : v;
  }
  return v;
};

type NodoSerializado = {
  type?: { resolvedName?: string } | string;
  props?: Record<string, unknown>;
  [k: string]: unknown;
};

/** Mapa de nodos serializados: { ROOT: {...}, id: {...} }. */
const traducirNodos = (
  nodos: Record<string, unknown>,
  resolver: Record<string, unknown>
): Record<string, unknown> | null => {
  let cambio = false;
  const out: Record<string, unknown> = {};
  for (const [id, valor] of Object.entries(nodos)) {
    const nodo = valor as NodoSerializado;
    if (!esObjetoPlano(nodo) || !esObjetoPlano(nodo.props)) {
      out[id] = valor;
      continue;
    }
    const props: Record<string, unknown> = { ...nodo.props };
    let cambioNodo = false;

    // 1) Lo que el árbol trae guardado.
    for (const k of Object.keys(props)) {
      const y = traducir(props[k]);
      if (y !== props[k]) {
        props[k] = y;
        cambioNodo = true;
      }
    }

    // 2) Lo que el árbol NO trae y Craft completaría con `craft.props` (en
    //    español). Se completa acá ya traducido; si no hay nada que traducir,
    //    se deja que Craft lo complete como siempre.
    const nombre = typeof nodo.type === 'object' ? nodo.type?.resolvedName : undefined;
    const componente = nombre ? (resolver[nombre] as { craft?: { props?: Record<string, unknown> } } | undefined) : undefined;
    const porDefecto = componente?.craft?.props;
    if (esObjetoPlano(porDefecto)) {
      for (const k of Object.keys(porDefecto)) {
        if (k in nodo.props) continue;
        const y = traducir(porDefecto[k]);
        if (y !== porDefecto[k]) {
          props[k] = y;
          cambioNodo = true;
        }
      }
    }

    out[id] = cambioNodo ? { ...nodo, props } : valor;
    if (cambioNodo) cambio = true;
  }
  return cambio ? out : null;
};

/**
 * Árbol del builder con los textos de fábrica en portugués. Acepta el árbol
 * como objeto o como JSON en texto, plano (`{ ROOT, … }`) o envuelto
 * (`{ nodes: { ROOT, … } }`). Si no hay nada que traducir —o el árbol no se
 * entiende— devuelve exactamente lo que recibió.
 */
export function traducirDefaultsDelArbolPt<T>(datos: T, resolver: Record<string, unknown>): T {
  let arbol: unknown = datos;
  if (typeof datos === 'string') {
    try {
      arbol = JSON.parse(datos);
    } catch {
      return datos;
    }
  }
  if (!esObjetoPlano(arbol)) return datos;

  if (esObjetoPlano(arbol.nodes)) {
    const nodos = traducirNodos(arbol.nodes, resolver);
    return nodos ? ({ ...arbol, nodes: nodos } as unknown as T) : datos;
  }
  const nodos = traducirNodos(arbol, resolver);
  return nodos ? (nodos as unknown as T) : datos;
}
